/**
 * Guitar Effects Engine
 * Web Audio API nodes for real-time guitar amp simulation and pedal effects
 */

// Distortion curve generator (waveshaper)
const makeDistortionCurve = (amount, type = 'soft') => {
  const samples = 44100;
  const curve = new Float32Array(samples);
  const deg = Math.PI / 180;
  
  for (let i = 0; i < samples; i++) {
    const x = (i * 2) / samples - 1;
    
    switch (type) {
      case 'hard':
        // Hard clipping
        curve[i] = Math.max(-1, Math.min(1, x * amount));
        break;
      case 'tube':
        // Tube-like saturation
        curve[i] = (3 + amount) * x * 20 * deg / (Math.PI + amount * Math.abs(x));
        break;
      case 'fuzz':
        // Fuzz effect (asymmetric clipping)
        if (x >= 0) {
          curve[i] = Math.tanh(x * amount);
        } else {
          curve[i] = Math.tanh(x * amount * 0.5);
        }
        break;
      default:
        // Soft clipping (default)
        curve[i] = ((3 + amount) * x * 20 * deg) / (Math.PI + amount * Math.abs(x));
    }
  }
  return curve;
};

// Create a simple cabinet IR simulation using filters
const createCabinetSimulation = (ctx, cabinetType) => {
  const input = ctx.createGain();
  const output = ctx.createGain();
  
  // Low-pass for speaker roll-off
  const lowPass = ctx.createBiquadFilter();
  lowPass.type = 'lowpass';
  
  // High-pass for tightness
  const highPass = ctx.createBiquadFilter();
  highPass.type = 'highpass';
  
  // Resonant peak (speaker character)
  const resonance = ctx.createBiquadFilter();
  resonance.type = 'peaking';
  
  // Cabinet-specific settings
  switch (cabinetType) {
    case '1x12':
      lowPass.frequency.value = 5000;
      lowPass.Q.value = 0.7;
      highPass.frequency.value = 100;
      resonance.frequency.value = 2500;
      resonance.gain.value = 3;
      resonance.Q.value = 1.5;
      break;
    case '2x12':
      lowPass.frequency.value = 4500;
      lowPass.Q.value = 0.8;
      highPass.frequency.value = 80;
      resonance.frequency.value = 2000;
      resonance.gain.value = 2;
      resonance.Q.value = 1.2;
      break;
    case '4x12':
      lowPass.frequency.value = 4000;
      lowPass.Q.value = 1.0;
      highPass.frequency.value = 60;
      resonance.frequency.value = 1800;
      resonance.gain.value = 4;
      resonance.Q.value = 1.0;
      break;
    case '4x12v30':
      lowPass.frequency.value = 5500;
      lowPass.Q.value = 0.9;
      highPass.frequency.value = 70;
      resonance.frequency.value = 3000;
      resonance.gain.value = 5;
      resonance.Q.value = 2.0;
      break;
    default: // direct
      lowPass.frequency.value = 20000;
      highPass.frequency.value = 20;
      resonance.gain.value = 0;
  }
  
  // Connect chain
  input.connect(highPass);
  highPass.connect(lowPass);
  lowPass.connect(resonance);
  resonance.connect(output);
  
  return { input, output, lowPass, highPass, resonance };
};

/**
 * Guitar Amp Simulator Node
 * Creates a full amp simulation chain
 */
export class GuitarAmpNode {
  constructor(ctx) {
    this.ctx = ctx;
    
    // Input gain (input level)
    this.inputGain = ctx.createGain();
    this.inputGain.gain.value = 1.0;
    
    // Pre-amp EQ
    this.preampBass = ctx.createBiquadFilter();
    this.preampBass.type = 'lowshelf';
    this.preampBass.frequency.value = 320;
    this.preampBass.gain.value = 0;
    
    this.preampMid = ctx.createBiquadFilter();
    this.preampMid.type = 'peaking';
    this.preampMid.frequency.value = 800;
    this.preampMid.Q.value = 1.0;
    this.preampMid.gain.value = 0;
    
    this.preampTreble = ctx.createBiquadFilter();
    this.preampTreble.type = 'highshelf';
    this.preampTreble.frequency.value = 3200;
    this.preampTreble.gain.value = 0;
    
    // Preamp distortion stage
    this.preampDrive = ctx.createWaveShaper();
    this.preampDrive.curve = makeDistortionCurve(1, 'tube');
    this.preampDrive.oversample = '4x';
    
    // Power amp stage (lighter saturation)
    this.powerAmpDrive = ctx.createWaveShaper();
    this.powerAmpDrive.curve = makeDistortionCurve(0.5, 'soft');
    this.powerAmpDrive.oversample = '2x';
    
    // Presence (high frequency boost after power amp)
    this.presence = ctx.createBiquadFilter();
    this.presence.type = 'highshelf';
    this.presence.frequency.value = 4000;
    this.presence.gain.value = 0;
    
    // Cabinet simulation
    this.cabinet = createCabinetSimulation(ctx, '4x12');
    
    // Master volume
    this.masterGain = ctx.createGain();
    this.masterGain.gain.value = 1.0;
    
    // Compensation gain - normalizes output volume across amp models
    this.compensationGain = ctx.createGain();
    this.compensationGain.gain.value = 1.0;
    
    // Output
    this.output = ctx.createGain();
    this.output.gain.value = 1.0;
    
    // Bypass
    this.bypassGain = ctx.createGain();
    this.bypassGain.gain.value = 0;
    this.bypassed = false;
    
    // Connect the chain
    this._connectChain();
    
    // Settings
    this.settings = {
      gain: 50,
      bass: 50,
      mid: 50,
      treble: 50,
      presence: 50,
      master: 70,
      cabinet: '4x12',
      ampModel: 'crunch',
    };
  }
  
  _connectChain() {
    // Main chain: input -> preamp EQ -> drive -> power amp -> presence -> cabinet -> master
    this.inputGain.connect(this.preampBass);
    this.preampBass.connect(this.preampMid);
    this.preampMid.connect(this.preampTreble);
    this.preampTreble.connect(this.preampDrive);
    this.preampDrive.connect(this.powerAmpDrive);
    this.powerAmpDrive.connect(this.presence);
    this.presence.connect(this.cabinet.input);
    this.cabinet.output.connect(this.masterGain);
    this.masterGain.connect(this.compensationGain);
    this.compensationGain.connect(this.output);
    
    // Bypass path
    this.inputGain.connect(this.bypassGain);
    this.bypassGain.connect(this.output);
  }
  
  connect(destination) {
    this.output.connect(destination);
    return this;
  }
  
  disconnect() {
    this.output.disconnect();
  }
  
  get input() {
    return this.inputGain;
  }
  
  setBypass(bypassed) {
    this.bypassed = bypassed;
    if (bypassed) {
      this.masterGain.gain.value = 0;
      this.bypassGain.gain.value = 1;
    } else {
      this.masterGain.gain.value = this.settings.master / 100;
      this.bypassGain.gain.value = 0;
    }
  }
  
  setAmpModel(model) {
    this.settings.ampModel = model;
    
    // Amp model presets - All 13 amp models
    // outputComp: keeps perceived volume roughly equal across models
    // Values are relative — clean amps near 1.0, high-gain amps reduced proportionally
    const presets = {
      clean:        { gain: 30, distType: 'soft', distAmount: 0.5,  outputComp: 1.00 },
      crunch:       { gain: 55, distType: 'tube', distAmount: 2,    outputComp: 0.80 },
      highGain:     { gain: 75, distType: 'tube', distAmount: 4,    outputComp: 0.65 },
      metal:        { gain: 90, distType: 'hard', distAmount: 6,    outputComp: 0.50 },
      acoustic:     { gain: 20, distType: 'soft', distAmount: 0.2,  outputComp: 1.00 },
      britishStack: { gain: 65, distType: 'tube', distAmount: 3,    outputComp: 0.72 },
      vintageDeluxe:{ gain: 40, distType: 'tube', distAmount: 1.5,  outputComp: 0.85 },
      modernMetal:  { gain: 85, distType: 'hard', distAmount: 5.5,  outputComp: 0.52 },
      bassDriver:   { gain: 45, distType: 'tube', distAmount: 1.8,  outputComp: 0.82 },
      jazzClean:    { gain: 25, distType: 'soft', distAmount: 0.3,  outputComp: 1.00 },
      bluesBreaker: { gain: 50, distType: 'tube', distAmount: 2.2,  outputComp: 0.78 },
      matchless:    { gain: 45, distType: 'tube', distAmount: 1.6,  outputComp: 0.84 },
      dualRecto:    { gain: 80, distType: 'hard', distAmount: 5,    outputComp: 0.55 },
    };
    
    const preset = presets[model] || presets.crunch;
    this.settings.gain = preset.gain;
    this.preampDrive.curve = makeDistortionCurve(preset.distAmount, preset.distType);
    
    // Apply output compensation to keep perceived volume consistent across models
    this.compensationGain.gain.value = preset.outputComp;
  }
  
  setCabinet(cabinetType) {
    this.settings.cabinet = cabinetType;
    
    // Disconnect old cabinet
    this.presence.disconnect();
    this.cabinet.output.disconnect();
    
    // Create new cabinet
    this.cabinet = createCabinetSimulation(this.ctx, cabinetType);
    
    // Reconnect through compensation gain
    this.presence.connect(this.cabinet.input);
    this.cabinet.output.connect(this.masterGain);
  }
  
  setControls(controls) {
    if (controls.gain !== undefined) {
      this.settings.gain = controls.gain;
      const gainAmount = (controls.gain / 100) * 5; // 0-5 range
      const distType = ['metal', 'modernMetal', 'dualRecto'].includes(this.settings.ampModel) ? 'hard' : 'tube';
      this.preampDrive.curve = makeDistortionCurve(gainAmount, distType);
      // Input gain range 0.8-2.0 — enough headroom without being extreme
      this.inputGain.gain.value = 0.8 + (controls.gain / 100) * 1.2;
    }
    
    if (controls.bass !== undefined) {
      this.settings.bass = controls.bass;
      this.preampBass.gain.value = ((controls.bass - 50) / 50) * 12; // -12 to +12 dB
    }
    
    if (controls.mid !== undefined) {
      this.settings.mid = controls.mid;
      this.preampMid.gain.value = ((controls.mid - 50) / 50) * 12;
    }
    
    if (controls.treble !== undefined) {
      this.settings.treble = controls.treble;
      this.preampTreble.gain.value = ((controls.treble - 50) / 50) * 12;
    }
    
    if (controls.presence !== undefined) {
      this.settings.presence = controls.presence;
      this.presence.gain.value = ((controls.presence - 50) / 50) * 8;
    }
    
    if (controls.master !== undefined) {
      this.settings.master = controls.master;
      if (!this.bypassed) {
        this.masterGain.gain.value = controls.master / 100;
      }
    }
  }
  
  getSettings() {
    return { ...this.settings };
  }
  
  dispose() {
    this.inputGain.disconnect();
    this.preampBass.disconnect();
    this.preampMid.disconnect();
    this.preampTreble.disconnect();
    this.preampDrive.disconnect();
    this.powerAmpDrive.disconnect();
    this.presence.disconnect();
    this.cabinet.input.disconnect();
    this.cabinet.output.disconnect();
    this.masterGain.disconnect();
    this.compensationGain.disconnect();
    this.output.disconnect();
    this.bypassGain.disconnect();
  }
}

/**
 * Guitar Pedal Effect Node
 * Individual pedal effect processor
 */
export class PedalEffectNode {
  constructor(ctx, type) {
    this.ctx = ctx;
    this.type = type;
    this.enabled = true;
    this.params = {};
    
    // Create nodes based on pedal type
    this._createNodes(type);
  }
  
  _createNodes(type) {
    const ctx = this.ctx;
    
    this.input = ctx.createGain();
    this.output = ctx.createGain();
    this.wetGain = ctx.createGain();
    this.dryGain = ctx.createGain();
    this.bypassGain = ctx.createGain();
    
    this.wetGain.gain.value = 1;
    this.dryGain.gain.value = 0;
    this.bypassGain.gain.value = 0;
    
    switch (type) {
      case 'distortion':
        this._createDistortion();
        break;
      case 'overdrive':
        this._createOverdrive();
        break;
      case 'fuzz':
        this._createFuzz();
        break;
      case 'chorus':
        this._createChorus();
        break;
      case 'phaser':
        this._createPhaser();
        break;
      case 'flanger':
        this._createFlanger();
        break;
      case 'tremolo':
        this._createTremolo();
        break;
      case 'compressor':
        this._createCompressor();
        break;
      case 'noisegate':
        this._createNoiseGate();
        break;
      case 'wah':
        this._createWah();
        break;
      case 'booster':
        this._createBooster();
        break;
      case 'autoWah':
        this._createAutoWah();
        break;
      case 'vibrato':
        this._createVibrato();
        break;
      case 'rotary':
        this._createRotary();
        break;
      case 'octaver':
        this._createOctaver();
        break;
      case 'pitchShifter':
        this._createPitchShifter();
        break;
      case 'delay':
        this._createDelay();
        break;
      case 'reverb':
        this._createReverb();
        break;
      case 'looper':
        this._createLooper();
        break;
      case 'eqPedal':
        this._createEQPedal();
        break;
      default:
        // Pass-through
        this.input.connect(this.output);
    }
    
    // Bypass path
    this.input.connect(this.bypassGain);
    this.bypassGain.connect(this.output);
  }
  
  _createDistortion() {
    this.waveshaper = this.ctx.createWaveShaper();
    this.waveshaper.curve = makeDistortionCurve(3, 'hard');
    this.waveshaper.oversample = '4x';
    
    this.toneFilter = this.ctx.createBiquadFilter();
    this.toneFilter.type = 'lowpass';
    this.toneFilter.frequency.value = 4000;
    
    this.levelGain = this.ctx.createGain();
    this.levelGain.gain.value = 0.70; // Distortion base level
    
    this.input.connect(this.waveshaper);
    this.waveshaper.connect(this.toneFilter);
    this.toneFilter.connect(this.levelGain);
    this.levelGain.connect(this.wetGain);
    this.wetGain.connect(this.output);
    
    this.params = { drive: 60, tone: 50, level: 70 };
  }
  
  _createOverdrive() {
    this.waveshaper = this.ctx.createWaveShaper();
    this.waveshaper.curve = makeDistortionCurve(2, 'tube');
    this.waveshaper.oversample = '2x';
    
    this.toneFilter = this.ctx.createBiquadFilter();
    this.toneFilter.type = 'lowpass';
    this.toneFilter.frequency.value = 5000;
    
    this.levelGain = this.ctx.createGain();
    this.levelGain.gain.value = 0.65; // Overdrive base level
    
    this.input.connect(this.waveshaper);
    this.waveshaper.connect(this.toneFilter);
    this.toneFilter.connect(this.levelGain);
    this.levelGain.connect(this.wetGain);
    this.wetGain.connect(this.output);
    
    this.params = { drive: 45, tone: 55, level: 65 };
  }
  
  _createFuzz() {
    this.waveshaper = this.ctx.createWaveShaper();
    this.waveshaper.curve = makeDistortionCurve(5, 'fuzz');
    this.waveshaper.oversample = '4x';
    
    this.toneFilter = this.ctx.createBiquadFilter();
    this.toneFilter.type = 'lowpass';
    this.toneFilter.frequency.value = 3500;
    
    this.levelGain = this.ctx.createGain();
    this.levelGain.gain.value = 0.55; // Fuzz base level (hottest pedal, still slightly reduced)
    
    this.input.connect(this.waveshaper);
    this.waveshaper.connect(this.toneFilter);
    this.toneFilter.connect(this.levelGain);
    this.levelGain.connect(this.wetGain);
    this.wetGain.connect(this.output);
    
    this.params = { fuzz: 70, tone: 45, volume: 60 };
  }
  
  _createChorus() {
    // Delay-based chorus
    this.delay = this.ctx.createDelay(0.1);
    this.delay.delayTime.value = 0.02;
    
    this.lfo = this.ctx.createOscillator();
    this.lfo.type = 'sine';
    this.lfo.frequency.value = 0.5;
    
    this.lfoGain = this.ctx.createGain();
    this.lfoGain.gain.value = 0.002;
    
    this.lfo.connect(this.lfoGain);
    this.lfoGain.connect(this.delay.delayTime);
    this.lfo.start();
    
    this.input.connect(this.delay);
    this.delay.connect(this.wetGain);
    this.input.connect(this.dryGain);
    this.dryGain.gain.value = 0.7;
    this.wetGain.gain.value = 0.7;
    this.wetGain.connect(this.output);
    this.dryGain.connect(this.output);
    
    this.params = { rate: 40, depth: 50, mix: 50 };
  }
  
  _createPhaser() {
    // All-pass filter chain for phaser
    this.allpassFilters = [];
    const stages = 4;
    
    let lastNode = this.input;
    for (let i = 0; i < stages; i++) {
      const allpass = this.ctx.createBiquadFilter();
      allpass.type = 'allpass';
      allpass.frequency.value = 1000 + i * 500;
      allpass.Q.value = 5;
      this.allpassFilters.push(allpass);
      lastNode.connect(allpass);
      lastNode = allpass;
    }
    
    this.lfo = this.ctx.createOscillator();
    this.lfo.type = 'sine';
    this.lfo.frequency.value = 0.4;
    
    this.lfoGain = this.ctx.createGain();
    this.lfoGain.gain.value = 500;
    
    this.lfo.connect(this.lfoGain);
    this.allpassFilters.forEach(filter => {
      this.lfoGain.connect(filter.frequency);
    });
    this.lfo.start();
    
    lastNode.connect(this.wetGain);
    this.input.connect(this.dryGain);
    this.dryGain.gain.value = 0.5;
    this.wetGain.gain.value = 0.5;
    this.wetGain.connect(this.output);
    this.dryGain.connect(this.output);
    
    this.params = { rate: 35, depth: 60, feedback: 50 };
  }
  
  _createFlanger() {
    this.delay = this.ctx.createDelay(0.02);
    this.delay.delayTime.value = 0.005;
    
    this.feedback = this.ctx.createGain();
    this.feedback.gain.value = 0.5;
    
    this.lfo = this.ctx.createOscillator();
    this.lfo.type = 'sine';
    this.lfo.frequency.value = 0.25;
    
    this.lfoGain = this.ctx.createGain();
    this.lfoGain.gain.value = 0.003;
    
    this.lfo.connect(this.lfoGain);
    this.lfoGain.connect(this.delay.delayTime);
    this.lfo.start();
    
    this.input.connect(this.delay);
    this.delay.connect(this.feedback);
    this.feedback.connect(this.delay);
    this.delay.connect(this.wetGain);
    this.input.connect(this.dryGain);
    this.dryGain.gain.value = 0.7;
    this.wetGain.gain.value = 0.7;
    this.wetGain.connect(this.output);
    this.dryGain.connect(this.output);
    
    this.params = { rate: 30, depth: 55, feedback: 65 };
  }
  
  _createTremolo() {
    this.lfo = this.ctx.createOscillator();
    this.lfo.type = 'sine';
    this.lfo.frequency.value = 5;
    
    this.lfoGain = this.ctx.createGain();
    this.lfoGain.gain.value = 0.5;
    
    this.tremoloGain = this.ctx.createGain();
    this.tremoloGain.gain.value = 1;
    
    // DC offset to keep volume above 0
    const dcOffset = this.ctx.createConstantSource();
    dcOffset.offset.value = 0.5;
    dcOffset.start();
    
    this.lfo.connect(this.lfoGain);
    this.lfoGain.connect(this.tremoloGain.gain);
    dcOffset.connect(this.tremoloGain.gain);
    this.lfo.start();
    
    this.input.connect(this.tremoloGain);
    this.tremoloGain.connect(this.wetGain);
    this.wetGain.connect(this.output);
    
    this.dcOffset = dcOffset;
    this.params = { rate: 50, depth: 60, wave: 50 };
  }
  
  _createCompressor() {
    this.compressor = this.ctx.createDynamicsCompressor();
    this.compressor.threshold.value = -24;
    this.compressor.knee.value = 30;
    this.compressor.ratio.value = 12;
    this.compressor.attack.value = 0.003;
    this.compressor.release.value = 0.25;
    
    this.makeupGain = this.ctx.createGain();
    this.makeupGain.gain.value = 1.3; // Compressor makeup gain
    
    this.input.connect(this.compressor);
    this.compressor.connect(this.makeupGain);
    this.makeupGain.connect(this.wetGain);
    this.wetGain.connect(this.output);
    
    this.params = { threshold: 55, attack: 30, level: 70 };
  }
  
  _createNoiseGate() {
    // Simple noise gate using dynamics compressor with high ratio
    this.gate = this.ctx.createDynamicsCompressor();
    this.gate.threshold.value = -40;
    this.gate.knee.value = 0;
    this.gate.ratio.value = 20;
    this.gate.attack.value = 0.001;
    this.gate.release.value = 0.1;
    
    this.input.connect(this.gate);
    this.gate.connect(this.wetGain);
    this.wetGain.connect(this.output);
    
    this.params = { threshold: 40, decay: 50 };
  }
  
  _createWah() {
    this.wahFilter = this.ctx.createBiquadFilter();
    this.wahFilter.type = 'bandpass';
    this.wahFilter.frequency.value = 1000;
    this.wahFilter.Q.value = 5;
    
    this.input.connect(this.wahFilter);
    this.wahFilter.connect(this.wetGain);
    this.wetGain.connect(this.output);
    
    this.params = { position: 50, range: 70, resonance: 60 };
  }
  
  _createBooster() {
    this.boostGain = this.ctx.createGain();
    this.boostGain.gain.value = 1.4; // Booster gain
    
    this.toneFilter = this.ctx.createBiquadFilter();
    this.toneFilter.type = 'highshelf';
    this.toneFilter.frequency.value = 3000;
    this.toneFilter.gain.value = 0;
    
    this.input.connect(this.boostGain);
    this.boostGain.connect(this.toneFilter);
    this.toneFilter.connect(this.wetGain);
    this.wetGain.connect(this.output);
    
    this.params = { boost: 50, tone: 50 };
  }
  
  _createAutoWah() {
    this.envelope = this.ctx.createBiquadFilter();
    this.envelope.type = 'bandpass';
    this.envelope.frequency.value = 500;
    this.envelope.Q.value = 4;
    
    this.input.connect(this.envelope);
    this.envelope.connect(this.wetGain);
    this.wetGain.connect(this.output);
    
    this.params = { sensitivity: 60, range: 70, resonance: 55 };
  }
  
  _createVibrato() {
    this.delay = this.ctx.createDelay(0.1);
    this.delay.delayTime.value = 0.005;
    
    this.lfo = this.ctx.createOscillator();
    this.lfo.type = 'sine';
    this.lfo.frequency.value = 5;
    
    this.lfoGain = this.ctx.createGain();
    this.lfoGain.gain.value = 0.002;
    
    this.lfo.connect(this.lfoGain);
    this.lfoGain.connect(this.delay.delayTime);
    this.lfo.start();
    
    this.input.connect(this.delay);
    this.delay.connect(this.wetGain);
    this.wetGain.connect(this.output);
    
    this.params = { rate: 45, depth: 40 };
  }
  
  _createRotary() {
    // Leslie speaker simulation
    this.delay1 = this.ctx.createDelay(0.1);
    this.delay2 = this.ctx.createDelay(0.1);
    this.delay1.delayTime.value = 0.002;
    this.delay2.delayTime.value = 0.003;
    
    this.lfo = this.ctx.createOscillator();
    this.lfo.type = 'sine';
    this.lfo.frequency.value = 1;
    
    this.lfoGain1 = this.ctx.createGain();
    this.lfoGain2 = this.ctx.createGain();
    this.lfoGain1.gain.value = 0.001;
    this.lfoGain2.gain.value = 0.0015;
    
    this.lfo.connect(this.lfoGain1);
    this.lfo.connect(this.lfoGain2);
    this.lfoGain1.connect(this.delay1.delayTime);
    this.lfoGain2.connect(this.delay2.delayTime);
    this.lfo.start();
    
    this.merger = this.ctx.createChannelMerger(2);
    
    this.input.connect(this.delay1);
    this.input.connect(this.delay2);
    this.delay1.connect(this.merger, 0, 0);
    this.delay2.connect(this.merger, 0, 1);
    this.merger.connect(this.wetGain);
    this.wetGain.connect(this.output);
    
    this.params = { speed: 50, drive: 30, mix: 60 };
  }
  
  _createOctaver() {
    // Simple octave effect using waveshaper for harmonics
    this.waveshaper = this.ctx.createWaveShaper();
    const samples = 44100;
    const curve = new Float32Array(samples);
    for (let i = 0; i < samples; i++) {
      const x = (i * 2) / samples - 1;
      curve[i] = Math.sign(x) * Math.pow(Math.abs(x), 0.5);
    }
    this.waveshaper.curve = curve;
    
    this.octaveGain = this.ctx.createGain();
    this.octaveGain.gain.value = 0.5;
    
    this.input.connect(this.waveshaper);
    this.waveshaper.connect(this.octaveGain);
    this.input.connect(this.dryGain);
    this.octaveGain.connect(this.wetGain);
    this.dryGain.gain.value = 0.7;
    this.wetGain.gain.value = 0.5;
    this.wetGain.connect(this.output);
    this.dryGain.connect(this.output);
    
    this.params = { octDown: 50, octUp: 30, dry: 70 };
  }
  
  _createPitchShifter() {
    // Simple pitch shift using delay modulation
    this.delay = this.ctx.createDelay(0.5);
    this.delay.delayTime.value = 0.1;
    
    this.input.connect(this.delay);
    this.delay.connect(this.wetGain);
    this.input.connect(this.dryGain);
    this.dryGain.gain.value = 0.5;
    this.wetGain.gain.value = 0.5;
    this.wetGain.connect(this.output);
    this.dryGain.connect(this.output);
    
    this.params = { shift: 0, fine: 0, mix: 50 };
  }
  
  _createDelay() {
    this.delay = this.ctx.createDelay(2.0);
    this.delay.delayTime.value = 0.4;
    
    this.feedback = this.ctx.createGain();
    this.feedback.gain.value = 0.35;
    
    this.input.connect(this.delay);
    this.delay.connect(this.feedback);
    this.feedback.connect(this.delay);
    this.delay.connect(this.wetGain);
    this.input.connect(this.dryGain);
    this.dryGain.gain.value = 0.7;
    this.wetGain.gain.value = 0.3;
    this.wetGain.connect(this.output);
    this.dryGain.connect(this.output);
    
    this.params = { time: 40, feedback: 35, mix: 30 };
  }
  
  _createReverb() {
    // Simple reverb using parallel delays
    this.delays = [];
    const delayTimes = [0.02, 0.03, 0.04, 0.05, 0.06];
    
    delayTimes.forEach(time => {
      const delay = this.ctx.createDelay(0.1);
      delay.delayTime.value = time;
      this.delays.push(delay);
      this.input.connect(delay);
      delay.connect(this.wetGain);
    });
    
    this.input.connect(this.dryGain);
    this.dryGain.gain.value = 0.75;
    this.wetGain.gain.value = 0.25;
    this.wetGain.connect(this.output);
    this.dryGain.connect(this.output);
    
    this.params = { decay: 50, predelay: 20, mix: 25 };
  }
  
  _createLooper() {
    // Simple pass-through for looper (actual looping would need more complex implementation)
    this.loopGain = this.ctx.createGain();
    this.loopGain.gain.value = 0.8;
    
    this.input.connect(this.loopGain);
    this.loopGain.connect(this.wetGain);
    this.wetGain.connect(this.output);
    
    this.params = { level: 80, feedback: 100, speed: 100 };
  }
  
  _createEQPedal() {
    this.lowFilter = this.ctx.createBiquadFilter();
    this.lowFilter.type = 'lowshelf';
    this.lowFilter.frequency.value = 300;
    this.lowFilter.gain.value = 0;
    
    this.midFilter = this.ctx.createBiquadFilter();
    this.midFilter.type = 'peaking';
    this.midFilter.frequency.value = 1000;
    this.midFilter.Q.value = 1;
    this.midFilter.gain.value = 0;
    
    this.highFilter = this.ctx.createBiquadFilter();
    this.highFilter.type = 'highshelf';
    this.highFilter.frequency.value = 3000;
    this.highFilter.gain.value = 0;
    
    this.input.connect(this.lowFilter);
    this.lowFilter.connect(this.midFilter);
    this.midFilter.connect(this.highFilter);
    this.highFilter.connect(this.wetGain);
    this.wetGain.connect(this.output);
    
    this.params = { low: 0, mid: 0, high: 0 };
  }
  
  connect(destination) {
    this.output.connect(destination);
    return this;
  }
  
  disconnect() {
    this.output.disconnect();
  }
  
  setEnabled(enabled) {
    this.enabled = enabled;
    if (enabled) {
      this.wetGain.gain.value = 1;
      this.dryGain.gain.value = this.type === 'chorus' || this.type === 'phaser' || this.type === 'flanger' ? 0.5 : 0;
      this.bypassGain.gain.value = 0;
    } else {
      this.wetGain.gain.value = 0;
      this.dryGain.gain.value = 0;
      this.bypassGain.gain.value = 1;
    }
  }
  
  setParams(params) {
    Object.assign(this.params, params);
    this._applyParams();
  }
  
  _applyParams() {
    const p = this.params;
    
    switch (this.type) {
      case 'distortion':
      case 'overdrive':
        if (p.drive !== undefined) {
          const amount = (p.drive / 100) * 5;
          this.waveshaper.curve = makeDistortionCurve(amount, this.type === 'distortion' ? 'hard' : 'tube');
        }
        if (p.tone !== undefined) {
          this.toneFilter.frequency.value = 2000 + (p.tone / 100) * 6000;
        }
        if (p.level !== undefined) {
          // Level range 0-1.0
          this.levelGain.gain.value = p.level / 100;
        }
        break;
        
      case 'fuzz':
        if (p.fuzz !== undefined) {
          const amount = (p.fuzz / 100) * 8;
          this.waveshaper.curve = makeDistortionCurve(amount, 'fuzz');
        }
        if (p.tone !== undefined) {
          this.toneFilter.frequency.value = 1500 + (p.tone / 100) * 4000;
        }
        if (p.volume !== undefined) {
          // Fuzz volume range 0-0.85
          this.levelGain.gain.value = (p.volume / 100) * 0.85;
        }
        break;
        
      case 'chorus':
        if (p.rate !== undefined) {
          this.lfo.frequency.value = 0.1 + (p.rate / 100) * 2;
        }
        if (p.depth !== undefined) {
          this.lfoGain.gain.value = (p.depth / 100) * 0.005;
        }
        if (p.mix !== undefined) {
          const wet = p.mix / 100;
          this.wetGain.gain.value = wet;
          this.dryGain.gain.value = 1 - wet * 0.5;
        }
        break;
        
      case 'phaser':
        if (p.rate !== undefined) {
          this.lfo.frequency.value = 0.1 + (p.rate / 100) * 1;
        }
        if (p.depth !== undefined) {
          this.lfoGain.gain.value = (p.depth / 100) * 1000;
        }
        break;
        
      case 'flanger':
        if (p.rate !== undefined) {
          this.lfo.frequency.value = 0.05 + (p.rate / 100) * 1;
        }
        if (p.depth !== undefined) {
          this.lfoGain.gain.value = (p.depth / 100) * 0.008;
        }
        if (p.feedback !== undefined) {
          this.feedback.gain.value = (p.feedback / 100) * 0.9;
        }
        break;
        
      case 'tremolo':
        if (p.rate !== undefined) {
          this.lfo.frequency.value = 1 + (p.rate / 100) * 15;
        }
        if (p.depth !== undefined) {
          this.lfoGain.gain.value = (p.depth / 100) * 0.5;
        }
        break;
        
      case 'compressor':
        if (p.threshold !== undefined) {
          this.compressor.threshold.value = -50 + (p.threshold / 100) * 40;
        }
        if (p.attack !== undefined) {
          this.compressor.attack.value = 0.001 + (p.attack / 100) * 0.1;
        }
        if (p.level !== undefined) {
          // Compressor makeup gain range 0.5-2.0
          this.makeupGain.gain.value = 0.5 + (p.level / 100) * 1.5;
        }
        break;
        
      case 'noisegate':
        if (p.threshold !== undefined) {
          this.gate.threshold.value = -60 + (p.threshold / 100) * 40;
        }
        if (p.decay !== undefined) {
          this.gate.release.value = 0.01 + (p.decay / 100) * 0.3;
        }
        break;
        
      case 'wah':
        if (p.position !== undefined) {
          this.wahFilter.frequency.value = 200 + (p.position / 100) * 2000;
        }
        if (p.resonance !== undefined) {
          this.wahFilter.Q.value = 1 + (p.resonance / 100) * 10;
        }
        break;
        
      case 'booster':
        if (p.boost !== undefined) {
          // Booster range 1.0-2.5
          this.boostGain.gain.value = 1.0 + (p.boost / 100) * 1.5;
        }
        if (p.tone !== undefined) {
          this.toneFilter.gain.value = ((p.tone - 50) / 50) * 6;
        }
        break;
        
      case 'autoWah':
        if (p.range !== undefined) {
          this.envelope.Q.value = 2 + (p.range / 100) * 8;
        }
        if (p.resonance !== undefined) {
          this.envelope.Q.value = 1 + (p.resonance / 100) * 10;
        }
        break;
        
      case 'vibrato':
        if (p.rate !== undefined) {
          this.lfo.frequency.value = 2 + (p.rate / 100) * 10;
        }
        if (p.depth !== undefined) {
          this.lfoGain.gain.value = (p.depth / 100) * 0.004;
        }
        break;
        
      case 'rotary':
        if (p.speed !== undefined) {
          this.lfo.frequency.value = 0.5 + (p.speed / 100) * 5;
        }
        break;
        
      case 'delay':
        if (p.time !== undefined) {
          this.delay.delayTime.value = 0.05 + (p.time / 100) * 1.5;
        }
        if (p.feedback !== undefined) {
          this.feedback.gain.value = (p.feedback / 100) * 0.8;
        }
        if (p.mix !== undefined) {
          this.wetGain.gain.value = p.mix / 100;
          this.dryGain.gain.value = 1 - (p.mix / 200);
        }
        break;
        
      case 'reverb':
        if (p.mix !== undefined) {
          this.wetGain.gain.value = p.mix / 100;
          this.dryGain.gain.value = 1 - (p.mix / 200);
        }
        break;
        
      case 'eqPedal':
        if (p.low !== undefined) {
          this.lowFilter.gain.value = p.low;
        }
        if (p.mid !== undefined) {
          this.midFilter.gain.value = p.mid;
        }
        if (p.high !== undefined) {
          this.highFilter.gain.value = p.high;
        }
        break;
    }
  }
  
  dispose() {
    if (this.lfo) {
      this.lfo.stop();
      this.lfo.disconnect();
    }
    if (this.dcOffset) {
      this.dcOffset.stop();
      this.dcOffset.disconnect();
    }
    this.input.disconnect();
    this.output.disconnect();
    this.wetGain.disconnect();
    this.dryGain.disconnect();
    this.bypassGain.disconnect();
  }
}

/**
 * Complete Guitar Effects Chain
 * Combines pedal board and amp simulator
 */
export class GuitarEffectsChain {
  constructor(ctx) {
    this.ctx = ctx;
    this.pedals = [];
    this.amp = null;
    this.enabled = false;
    
    // Create input/output nodes
    this.input = ctx.createGain();
    this.output = ctx.createGain();
    this.bypassGain = ctx.createGain();
    
    this.input.gain.value = 1;
    this.output.gain.value = 1;
    this.bypassGain.gain.value = 1;
    
    // Default bypass state
    this.input.connect(this.bypassGain);
    this.bypassGain.connect(this.output);
  }
  
  enable() {
    if (this.enabled) return;
    this.enabled = true;
    this.bypassGain.gain.value = 0;
    this._rebuildChain();
  }
  
  disable() {
    if (!this.enabled) return;
    this.enabled = false;
    
    // Disconnect effects
    this.input.disconnect();
    this.input.connect(this.bypassGain);
    this.bypassGain.gain.value = 1;
    
    // Dispose pedals
    this.pedals.forEach(p => p.dispose());
    this.pedals = [];
    
    if (this.amp) {
      this.amp.dispose();
      this.amp = null;
    }
  }
  
  setAmpEnabled(enabled, settings = {}) {
    if (enabled && !this.amp) {
      this.amp = new GuitarAmpNode(this.ctx);
      if (settings.ampModel) this.amp.setAmpModel(settings.ampModel);
      if (settings.cabinet) this.amp.setCabinet(settings.cabinet);
      if (settings.controls) this.amp.setControls(settings.controls);
    } else if (!enabled && this.amp) {
      this.amp.dispose();
      this.amp = null;
    }
    
    if (this.enabled) {
      this._rebuildChain();
    }
  }
  
  setAmpSettings(settings) {
    if (!this.amp) return;
    
    if (settings.ampModel) this.amp.setAmpModel(settings.ampModel);
    if (settings.cabinet) this.amp.setCabinet(settings.cabinet);
    if (settings.controls) this.amp.setControls(settings.controls);
    if (settings.bypassed !== undefined) this.amp.setBypass(settings.bypassed);
  }
  
  setPedals(pedalConfigs) {
    // Clear existing pedals
    this.pedals.forEach(p => p.dispose());
    this.pedals = [];
    
    // Create new pedals
    pedalConfigs.forEach(config => {
      const pedal = new PedalEffectNode(this.ctx, config.type);
      pedal.setEnabled(config.enabled !== false);
      if (config.params) pedal.setParams(config.params);
      this.pedals.push(pedal);
    });
    
    if (this.enabled) {
      this._rebuildChain();
    }
  }
  
  updatePedal(index, config) {
    if (index >= 0 && index < this.pedals.length) {
      const pedal = this.pedals[index];
      if (config.enabled !== undefined) pedal.setEnabled(config.enabled);
      if (config.params) pedal.setParams(config.params);
    }
  }
  
  _rebuildChain() {
    // Disconnect everything
    this.input.disconnect();
    
    // Build chain: input -> pedals -> amp -> output
    let lastNode = this.input;
    
    // Connect pedals in order
    this.pedals.forEach(pedal => {
      lastNode.connect(pedal.input);
      lastNode = pedal;
    });
    
    // Connect amp if present
    if (this.amp) {
      lastNode.connect(this.amp.input);
      this.amp.connect(this.output);
    } else {
      lastNode.connect(this.output);
    }
  }
  
  connect(destination) {
    this.output.connect(destination);
    return this;
  }
  
  disconnect() {
    this.output.disconnect();
  }
  
  dispose() {
    this.disable();
    this.input.disconnect();
    this.output.disconnect();
    this.bypassGain.disconnect();
  }
  
  getSettings() {
    return {
      enabled: this.enabled,
      amp: this.amp ? this.amp.getSettings() : null,
      pedals: this.pedals.map((p, i) => ({
        type: p.type,
        enabled: p.enabled,
        params: { ...p.params },
      })),
    };
  }
}

export default {
  GuitarAmpNode,
  PedalEffectNode,
  GuitarEffectsChain,
};
